import {
  collection,
  onSnapshot,
  orderBy,
  query,
  startAt,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore';
import { isNetworkError } from '../../utils/firebaseErrors';
import {
  normalizeBackofficeReportChangeSignal,
  type BackofficeReportChangeSignal,
} from './reportChangeSignal';
import type { ReportsInvalidationGateway } from './reportsInvalidationGateway';

const REPORTS_SIGNAL_ROOT = ['systemSignals', 'backofficeReportChanges', 'messages'] as const;

interface RealTimeListener {
  unsubscribe: Unsubscribe;
  lastUpdate: number;
  retryCount: number;
  retryTimeoutId: ReturnType<typeof setTimeout> | null;
  resumeFromMs: number;
  seenSignalIds: Set<string>;
}

const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;

export interface FirestoreReportsInvalidationGateway extends ReportsInvalidationGateway {
  getLastUpdateForListener: (listenerId: string) => number | null;
  isListenerActive: (listenerId: string) => boolean;
  countActiveListeners: () => number;
}

export const createFirestoreReportsInvalidationGateway = (
  reportsDb: Firestore
): FirestoreReportsInvalidationGateway => {
  const activeListeners = new Map<string, RealTimeListener>();

  const buildReportsChangeSignalQuery = (sinceMs: number) =>
    query(
      collection(reportsDb, ...REPORTS_SIGNAL_ROOT),
      orderBy('occurredAtMs', 'asc'),
      startAt(sinceMs)
    );

  const clearRetryTimeout = (listenerId: string): void => {
    const listener = activeListeners.get(listenerId);
    if (listener?.retryTimeoutId) {
      clearTimeout(listener.retryTimeoutId);
      listener.retryTimeoutId = null;
    }
  };

  const calculateBackoffDelay = (retryCount: number): number => {
    const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount);
    return Math.min(delay, MAX_RETRY_DELAY_MS);
  };

  const setupInternalListener = (
    listenerId: string,
    listener: RealTimeListener,
    callback: (signal: BackofficeReportChangeSignal) => void
  ): Unsubscribe =>
    onSnapshot(
      buildReportsChangeSignalQuery(listener.resumeFromMs),
      (snapshot) => {
        const currentListener = activeListeners.get(listenerId);
        if (!currentListener) {
          return;
        }

        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            return;
          }

          const signal = normalizeBackofficeReportChangeSignal(change.doc.data(), change.doc.id);
          if (!signal || currentListener.seenSignalIds.has(signal.signalId)) {
            return;
          }

          currentListener.seenSignalIds.add(signal.signalId);
          currentListener.resumeFromMs = Math.max(
            currentListener.resumeFromMs,
            signal.occurredAtMs
          );
          currentListener.lastUpdate = Date.now();
          callback(signal);
        });

        clearRetryTimeout(listenerId);

        if (currentListener.retryCount > 0) {
          currentListener.retryCount = 0;
        }
      },
      (error) => {
        const retryable = isNetworkError(error);
        if (!retryable) {
          console.error('[reports-signal] listener error', { retryable: false });
          return;
        }

        const currentListener = activeListeners.get(listenerId);
        if (!currentListener) {
          return;
        }

        if (currentListener.retryTimeoutId) {
          return;
        }

        console.error('[reports-signal] listener error', { retryable: true });

        if (currentListener.retryCount >= MAX_RETRIES) {
          console.error('[reports-signal] retry exhausted', { maxRetries: MAX_RETRIES });
          return;
        }

        const delay = calculateBackoffDelay(currentListener.retryCount);
        currentListener.retryCount++;
        console.log('[reports-signal] reconnect scheduled', {
          attempt: currentListener.retryCount,
          delayMs: delay,
        });

        currentListener.retryTimeoutId = setTimeout(() => {
          const refreshedListener = activeListeners.get(listenerId);
          if (!refreshedListener) {
            return;
          }

          refreshedListener.retryTimeoutId = null;

          console.log('[reports-signal] reconnect attempt', {
            attempt: refreshedListener.retryCount,
          });
          try {
            refreshedListener.unsubscribe();
          } catch {
            console.error('[reports-signal] listener cleanup failed', { phase: 'reconnect' });
          }
          refreshedListener.unsubscribe = setupInternalListener(
            listenerId,
            refreshedListener,
            callback
          );
        }, delay);
      }
    );

  const removeReportsListener = (listenerId: string): void => {
    const listener = activeListeners.get(listenerId);
    if (!listener) {
      return;
    }

    clearRetryTimeout(listenerId);
    listener.unsubscribe();
    activeListeners.delete(listenerId);
  };

  const setupReportsListener = (
    listenerId: string,
    callback: (signal: BackofficeReportChangeSignal) => void
  ): Unsubscribe => {
    removeReportsListener(listenerId);

    try {
      const listener: RealTimeListener = {
        unsubscribe: () => undefined,
        lastUpdate: Date.now(),
        retryCount: 0,
        retryTimeoutId: null,
        resumeFromMs: Date.now(),
        seenSignalIds: new Set<string>(),
      };

      activeListeners.set(listenerId, listener);
      console.log('[reports-signal] listener subscribed', {
        activeListenerCount: activeListeners.size,
      });
      listener.unsubscribe = setupInternalListener(listenerId, listener, callback);

      return () => removeReportsListener(listenerId);
    } catch (error) {
      activeListeners.delete(listenerId);
      console.error('[reports-signal] listener setup failed', {});
      throw error;
    }
  };

  return {
    setupReportsListener,
    removeReportsListener,
    getLastUpdateForListener: (listenerId) => activeListeners.get(listenerId)?.lastUpdate ?? null,
    isListenerActive: (listenerId) => activeListeners.has(listenerId),
    countActiveListeners: () => activeListeners.size,
    cleanupAllListeners: () => {
      const count = activeListeners.size;
      activeListeners.forEach((listener, listenerId) => {
        try {
          clearRetryTimeout(listenerId);
          listener.unsubscribe();
        } catch {
          console.error('[reports-signal] listener cleanup failed', { phase: 'session-cleanup' });
        }
      });
      activeListeners.clear();
      return count;
    },
  };
};
